// ─────────────────────────────────────────────────────────────────────────────
// UNA SOLA AUTORIDAD DE CUPO · paquetes_cotizacion.application_count
//
// Gemelo de `supabase/functions/_shared/cupo-de-paquete.ts`. Si uno cambia, el otro
// tambien: una prueba lo comprueba.
//
// Aqui la fila del catalogo YA esta cargada -`AppContext` trae `paquetes_cotizacion`-,
// asi que este modulo es puro: recibe la fila y decide.
//
// ── POR QUE EXISTE ────────────────────────────────────────────────────────
//
// Habia CUATRO autoridades de cupo y la canonica no la leia nadie del camino del dinero:
//
//   paquetes_cotizacion.application_count   solo los dos admin
//   package_rules.included_applications     el cobro de extras (espacio de nombres legado)
//   DEFAULT_PACKAGE_RULES.includedApplications   AdminBriefs, Kanban, ClientPortal
//   proyectos.package_app_limit             copia materializada
//
// Ahora hay una, y las otras tres son espejo o historia.
//
// ── NULL NO ES CERO ───────────────────────────────────────────────────────
//
// `corporate_web` tiene `application_count` NULL porque la web NO usa aplicaciones. Eso lo
// decide el tipo de servicio, no el numero: `servicioUsaAplicaciones` excluye `web` a
// proposito, y `webQuoteSinAplicaciones.test.js` documenta el incidente real que lo motivo.
// Un 0 diria «ninguna de las que podria tener», que es otra cosa.
// ─────────────────────────────────────────────────────────────────────────────
import { servicioUsaAplicaciones } from './appCatalog';
// EL MISMO REGISTRO, no otro. Una fila de `paquetes_cotizacion` lleva el precio y el
// `application_count`: son la misma fila. `AppContext` la carga una vez y la registra ahi.
// Un segundo registro serian dos cargas y dos verdades.
import { catalogoCanonicoRegistrado } from './precioDePaquete';

export const CUPO_AUTORIDAD = Object.freeze({
  /** El cupo salio de la tabla canonica. */
  CANONICA: 'paquetes_cotizacion.application_count',
  /** El servicio no tiene etapa de aplicaciones (hoy solo `web`). `limite` es null. */
  SIN_APLICACIONES: 'SERVICE_WITHOUT_APPLICATIONS',
  /** No se pudo resolver el paquete vendido. `limite` es null y hay que negarse. */
  SIN_RESOLVER: 'UNRESOLVED',
});

const texto = (v) => (v === null || v === undefined ? '' : String(v)).trim();

/**
 * EL CUPO, CON LA FILA DEL CATALOGO EN LA MANO.
 *
 * @param fila      la fila de `paquetes_cotizacion`, o null si no se encontro
 * @param opciones  { packageId, serviceType } — el id que el cliente acepto y el tipo de
 *                  servicio de la venta. `family` de la fila es el ultimo respaldo del tipo.
 * @returns { limite, autoridad, packageId, serviceType, resuelto }
 *          `resuelto` es verdadero SOLO cuando `limite` es un numero fiable.
 */
export function cupoDesdeLaFila(fila, opciones = {}) {
  const packageId = texto(opciones.packageId ?? fila?.id);
  const serviceType = (texto(opciones.serviceType) || texto(fila?.family)).toLowerCase();
  const no = (autoridad) => ({ limite: null, autoridad, packageId, serviceType, resuelto: false });

  if (!servicioUsaAplicaciones(serviceType)) return no(CUPO_AUTORIDAD.SIN_APLICACIONES);
  if (!packageId || !fila) return no(CUPO_AUTORIDAD.SIN_RESOLVER);

  const crudo = fila.application_count ?? fila.applicationCount;
  if (crudo === null || crudo === undefined || crudo === '') return no(CUPO_AUTORIDAD.SIN_RESOLVER);

  const n = Number(crudo);
  if (!Number.isFinite(n) || n < 0) return no(CUPO_AUTORIDAD.SIN_RESOLVER);

  return {
    limite: Math.trunc(n),
    autoridad: CUPO_AUTORIDAD.CANONICA,
    packageId,
    serviceType,
    resuelto: true,
  };
}

/**
 * EL CUPO BUSCANDO LA FILA EN UN CATALOGO YA CARGADO.
 *
 * `catalogo` es la lista de paquetes (la que `AppContext` o `quoteCatalog` ya tienen). No
 * hace ninguna peticion: si el paquete no esta en la lista, el resultado es UNRESOLVED, que
 * se niega. Un catalogo a medio cargar no inventa un cupo.
 */
export function cupoIncluidoDelPaquete(catalogo, { packageId, serviceType } = {}) {
  const id = texto(packageId);
  const lista = Array.isArray(catalogo) ? catalogo : [];
  const fila = id ? lista.find((p) => texto(p?.id) === id) || null : null;
  return cupoDesdeLaFila(fila, { packageId: id, serviceType });
}

/**
 * EL CUPO PARA PINTAR, cuando la pantalla necesita un numero y no un null.
 *
 * Devuelve `{ texto, limite }`. Una pantalla NO debe inventar un cupo: si no resuelve, dice
 * que no lo sabe. Es lo mismo que la regla del precio -«un precio viejo es peor que ningun
 * precio»- aplicada al cupo.
 */
export function cupoParaPintar(cupo) {
  if (!cupo || !cupo.resuelto) {
    if (cupo && cupo.autoridad === CUPO_AUTORIDAD.SIN_APLICACIONES) {
      return { texto: 'Este servicio no usa aplicaciones', limite: null };
    }
    return { texto: 'Cupo sin confirmar', limite: null };
  }
  return { texto: `${cupo.limite} incluidas`, limite: cupo.limite };
}

// ─────────────────────────────────────────────────────────────────────────────
// EL CUPO DESDE UNA PANTALLA
//
// Los consumidores del frontend manejan NOMBRES de paquete —«Select», «Brand Identity»—
// porque el estudio los elige de una lista de texto. El nombre no aporta un cupo por si
// mismo: es una clave de busqueda contra el catalogo canonico, exactamente como en
// `precioDePaquete`.
//
// Y cuando no resuelve, devuelve `null`. Quien decida un cobro con eso tiene que negarse:
// el fallback silencioso a «Starter = 3» es el defecto que C1 cerro en el borde y que
// `getPackageRule` mantenia vivo aqui.
// ─────────────────────────────────────────────────────────────────────────────

/** Forma comparable de un nombre: minusculas, sin tildes, sin puntuacion. */
const clave = (v) => texto(v)
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/**
 * EL CUPO USANDO EL CATALOGO YA REGISTRADO.
 *
 * Prefiere `packageId`. `packageName` es SOLO una clave de busqueda. Si el registro esta
 * vacio —el catalogo no llego— todo resuelve UNRESOLVED, que se niega.
 */
export function cupoCanonicoRegistrado({ packageId, packageName, serviceType } = {}) {
  const lista = catalogoCanonicoRegistrado();
  const id = texto(packageId);
  let fila = id ? lista.find((p) => texto(p?.id) === id) || null : null;

  if (!fila && texto(packageName)) {
    const k = clave(packageName);
    fila = lista.find((p) => clave(p?.nombre) === k || clave(p?.name) === k || clave(p?.id) === k) || null;
  }
  return cupoDesdeLaFila(fila, {
    packageId: id || texto(fila?.id),
    serviceType: texto(serviceType) || texto(fila?.family),
  });
}

/**
 * EL NUMERO O NADA, para los consumidores que esperaban un numero.
 *
 * `null`, nunca 0 y nunca 3. Quien lo llame tiene que decidir que hacer con `null`, y eso es
 * el punto: antes recibia el cupo de un paquete que no era el suyo y no lo sabia.
 */
export function cupoCanonicoONull(opciones = {}) {
  const c = cupoCanonicoRegistrado(opciones);
  return c.resuelto ? c.limite : null;
}

/** ¿Hay autoridad de cupo cargada? Una pantalla puede necesitar saberlo antes de decidir. */
export function hayAutoridadDeCupo() {
  return catalogoCanonicoRegistrado().length > 0;
}
